import React, { useState, useEffect, useRef } from 'react';
import { useAuth, useAuthPrompt } from '../App';
import { 
  getVaultDocuments, 
  uploadVaultDocument, 
  deleteVaultDocument,
  downloadVaultDocument,
  getShoeboxInbox,
  getShoeboxEmailAddress,
  approveShoeboxItem,
  rejectShoeboxItem,
  deleteShoeboxItem
} from '../lib/api';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import TaxFormsSearch from '../components/TaxFormsSearch';
import { toast } from 'sonner';
import {
  FolderOpen,
  Upload,
  FileText,
  Image,
  File,
  Search,
  Grid,
  List,
  Loader2,
  Download,
  Trash2,
  Eye,
  CheckCircle,
  X,
  FileStack,
  Receipt,
  Calculator,
  Building2,
  BookOpen,
  Filter,
  Plus,
  Mail,
  Inbox,
  Copy,
  RefreshCw,
  Sparkles,
  DollarSign,
  Calendar,
  Tag,
  XCircle,
  ChevronRight,
  ScanLine,
  Wand2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatFileSize(bytes) {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

function formatDateTime(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatCurrency(amount) {
  if (!amount) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(Math.abs(amount));
}

function getFileIcon(type) {
  if (type?.startsWith('image/')) return Image;
  if (type?.includes('pdf')) return FileText;
  return File;
}

// My Documents Tab - User uploaded documents with OCR
function MyDocumentsTab({ auth, authPrompt }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDoc, setSelectedDoc] = useState(null);
  const fileInputRef = useRef(null);

  const categories = [
    { value: 'all', label: 'All' },
    { value: 'receipt', label: 'Receipts' },
    { value: 'invoice', label: 'Invoices' },
    { value: 'contract', label: 'Contracts' },
    { value: 'tax', label: 'Tax' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    fetchDocuments();
  }, []);

  async function fetchDocuments() {
    setLoading(true);
    try {
      const res = await getVaultDocuments();
      setDocuments(res.data.documents || []);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleUpload(event) {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    if (!auth?.user) {
      authPrompt('Upload Document');
      return;
    }

    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', selectedCategory === 'all' ? 'other' : selectedCategory);
        formData.append('auto_ocr', 'true'); // Enable auto OCR
        await uploadVaultDocument(formData);
      }
      toast.success(`${files.length} document(s) uploaded`);
      fetchDocuments();
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleOCR(doc) {
    if (!auth?.user) {
      authPrompt('Process Document');
      return;
    }
    
    setProcessing(doc.id);
    try {
      const response = await axios.post(`${API_URL}/api/documents/${doc.id}/ocr`, {}, { withCredentials: true });
      const result = response.data;
      
      if (result.success) {
        toast.success('Document processed! Data extracted.');
        // Update the document in state with OCR data
        setDocuments(prev => prev.map(d => 
          d.id === doc.id ? { ...d, ...result.ocr_data, ocr_processed: true } : d
        ));
        setSelectedDoc(prev => prev?.id === doc.id ? { ...prev, ...result.ocr_data, ocr_processed: true } : prev);
      } else {
        toast.error(result.message || 'OCR processing failed');
      }
    } catch (error) {
      toast.error('Failed to process document');
    } finally {
      setProcessing(null);
    }
  }

  async function handleDelete(docId) {
    if (!window.confirm('Delete this document?')) return;
    try {
      await deleteVaultDocument(docId);
      setDocuments(prev => prev.filter(d => d.id !== docId));
      if (selectedDoc?.id === docId) setSelectedDoc(null);
      toast.success('Document deleted');
    } catch (error) {
      toast.error('Delete failed');
    }
  }

  async function handleDownload(doc) {
    try {
      const res = await downloadVaultDocument(doc.id);
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Download failed');
    }
  }

  const filteredDocs = documents.filter(doc => {
    const matchesSearch = !searchTerm || 
      doc.filename?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.ocr_vendor?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const stats = {
    total: documents.length,
    processed: documents.filter(d => d.ocr_processed).length,
    receipts: documents.filter(d => d.category === 'receipt').length,
    invoices: documents.filter(d => d.category === 'invoice').length
  };

  return (
    <div className="space-y-6">
      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-premium bg-gradient-to-br from-[hsl(var(--accent))]/10 to-transparent">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-sm text-muted-foreground">Total Documents</p>
              </div>
              <FileStack className="w-8 h-8 text-[hsl(var(--accent))] opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-premium">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{stats.processed}</p>
                <p className="text-sm text-muted-foreground">AI Processed</p>
              </div>
              <Sparkles className="w-8 h-8 text-purple-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-premium">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{stats.receipts}</p>
                <p className="text-sm text-muted-foreground">Receipts</p>
              </div>
              <Receipt className="w-8 h-8 text-emerald-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-premium">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{stats.invoices}</p>
                <p className="text-sm text-muted-foreground">Invoices</p>
              </div>
              <FileText className="w-8 h-8 text-blue-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex items-center gap-3 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex items-center gap-1 bg-muted/30 rounded-lg p-1">
            {categories.map(cat => (
              <Button
                key={cat.value}
                variant="ghost"
                size="sm"
                onClick={() => setSelectedCategory(cat.value)}
                className={`text-xs ${selectedCategory === cat.value ? 'bg-[hsl(var(--accent))]/20 text-[hsl(var(--accent))]' : ''}`}
              >
                {cat.label}
              </Button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-muted/30 rounded-lg p-1">
            <Button variant="ghost" size="sm" onClick={() => setViewMode('grid')} className={viewMode === 'grid' ? 'bg-muted' : ''}>
              <Grid className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setViewMode('list')} className={viewMode === 'list' ? 'bg-muted' : ''}>
              <List className="w-4 h-4" />
            </Button>
          </div>
          <Button 
            onClick={() => fileInputRef.current?.click()} 
            disabled={uploading}
            className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-black"
          >
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Upload
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleUpload}
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex gap-6">
        {/* Documents Grid/List */}
        <div className={`flex-1 ${selectedDoc ? 'max-w-[60%]' : ''}`}>
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredDocs.length === 0 ? (
            <Card className="card-premium">
              <CardContent className="py-16 text-center">
                <FolderOpen className="w-16 h-16 mx-auto text-muted-foreground mb-4 opacity-50" />
                <p className="text-lg font-medium">No documents found</p>
                <p className="text-muted-foreground mb-4">
                  {searchTerm ? 'Try a different search term' : 'Upload documents to get started. AI will auto-extract data.'}
                </p>
                <Button onClick={() => fileInputRef.current?.click()} className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-black">
                  <Upload className="w-4 h-4 mr-2" /> Upload Document
                </Button>
              </CardContent>
            </Card>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredDocs.map((doc) => {
                const FileIcon = getFileIcon(doc.file_type);
                const isSelected = selectedDoc?.id === doc.id;
                return (
                  <Card 
                    key={doc.id} 
                    className={`card-premium cursor-pointer group transition-all ${isSelected ? 'border-[hsl(var(--accent))] ring-2 ring-[hsl(var(--accent))]/20' : 'hover:border-[hsl(var(--accent))]/50'}`}
                    onClick={() => setSelectedDoc(doc)}
                  >
                    <CardContent className="p-3">
                      <div className="aspect-square rounded-lg bg-muted/30 flex items-center justify-center mb-2 relative overflow-hidden">
                        {doc.file_type?.startsWith('image/') && doc.thumbnail ? (
                          <img src={`data:${doc.file_type};base64,${doc.thumbnail}`} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <FileIcon className="w-10 h-10 text-muted-foreground" />
                        )}
                        {doc.ocr_processed && (
                          <div className="absolute top-1 right-1">
                            <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-xs px-1">
                              <Sparkles className="w-3 h-3" />
                            </Badge>
                          </div>
                        )}
                      </div>
                      <p className="font-medium text-sm truncate" title={doc.filename}>{doc.filename}</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-muted-foreground">{formatFileSize(doc.file_size)}</span>
                        <Badge variant="outline" className="text-xs capitalize">{doc.category || 'other'}</Badge>
                      </div>
                      {doc.ocr_amount && (
                        <p className="text-sm font-semibold text-[hsl(var(--accent))] mt-1">{formatCurrency(doc.ocr_amount)}</p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className="card-premium">
              <CardContent className="p-0">
                <div className="divide-y divide-border">
                  {filteredDocs.map((doc) => {
                    const FileIcon = getFileIcon(doc.file_type);
                    const isSelected = selectedDoc?.id === doc.id;
                    return (
                      <div 
                        key={doc.id} 
                        className={`flex items-center justify-between p-4 cursor-pointer transition-colors ${isSelected ? 'bg-[hsl(var(--accent))]/10' : 'hover:bg-muted/30'}`}
                        onClick={() => setSelectedDoc(doc)}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-muted/30 flex items-center justify-center relative">
                            <FileIcon className="w-5 h-5 text-muted-foreground" />
                            {doc.ocr_processed && (
                              <Sparkles className="w-3 h-3 text-purple-400 absolute -top-1 -right-1" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium">{doc.filename}</p>
                            <div className="flex items-center gap-3 text-sm text-muted-foreground">
                              <span>{formatFileSize(doc.file_size)}</span>
                              <span>{formatDate(doc.created_at)}</span>
                              {doc.ocr_vendor && <span className="text-[hsl(var(--accent))]">{doc.ocr_vendor}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {doc.ocr_amount && (
                            <span className="font-semibold">{formatCurrency(doc.ocr_amount)}</span>
                          )}
                          <Badge variant="outline" className="text-xs capitalize">{doc.category || 'other'}</Badge>
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Document Detail Panel */}
        {selectedDoc && (
          <Card className="card-premium w-[40%] sticky top-4 h-fit">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg truncate">{selectedDoc.filename}</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setSelectedDoc(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Preview */}
              <div className="aspect-video rounded-lg bg-muted/30 flex items-center justify-center overflow-hidden">
                {selectedDoc.file_type?.startsWith('image/') && selectedDoc.file_data ? (
                  <img src={`data:${selectedDoc.file_type};base64,${selectedDoc.file_data}`} alt="" className="max-w-full max-h-full object-contain" />
                ) : (
                  <FileText className="w-16 h-16 text-muted-foreground" />
                )}
              </div>

              {/* OCR Data */}
              {selectedDoc.ocr_processed ? (
                <div className="space-y-3 p-4 rounded-lg bg-purple-500/5 border border-purple-500/20">
                  <div className="flex items-center gap-2 text-purple-400">
                    <Sparkles className="w-4 h-4" />
                    <span className="font-medium">AI Extracted Data</span>
                  </div>
                  {selectedDoc.ocr_vendor && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground flex items-center gap-2"><Tag className="w-4 h-4" /> Vendor</span>
                      <span className="font-medium">{selectedDoc.ocr_vendor}</span>
                    </div>
                  )}
                  {selectedDoc.ocr_amount && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground flex items-center gap-2"><DollarSign className="w-4 h-4" /> Amount</span>
                      <span className="font-semibold text-[hsl(var(--accent))]">{formatCurrency(selectedDoc.ocr_amount)}</span>
                    </div>
                  )}
                  {selectedDoc.ocr_date && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground flex items-center gap-2"><Calendar className="w-4 h-4" /> Date</span>
                      <span className="font-medium">{selectedDoc.ocr_date}</span>
                    </div>
                  )}
                  {selectedDoc.ocr_category && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Category</span>
                      <Badge variant="outline">{selectedDoc.ocr_category}</Badge>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-4 rounded-lg bg-muted/30 text-center">
                  <Wand2 className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground mb-3">Extract data with AI</p>
                  <Button 
                    onClick={() => handleOCR(selectedDoc)}
                    disabled={processing === selectedDoc.id}
                    className="bg-purple-500 hover:bg-purple-600 text-white"
                  >
                    {processing === selectedDoc.id ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                    ) : (
                      <><ScanLine className="w-4 h-4 mr-2" /> Extract Data</>
                    )}
                  </Button>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => handleDownload(selectedDoc)}>
                  <Download className="w-4 h-4 mr-2" /> Download
                </Button>
                <Button variant="outline" className="text-red-400 hover:text-red-300" onClick={() => handleDelete(selectedDoc.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              {/* Metadata */}
              <div className="pt-3 border-t border-border text-sm text-muted-foreground space-y-1">
                <p>Size: {formatFileSize(selectedDoc.file_size)}</p>
                <p>Uploaded: {formatDateTime(selectedDoc.created_at)}</p>
                <p>Type: {selectedDoc.file_type}</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// Email Inbox Tab (Digital Shoebox)
function EmailInboxTab({ auth, authPrompt }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shoeboxEmail, setShoeboxEmail] = useState(null);
  const [instructions, setInstructions] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      setLoading(true);
      const [inboxRes, emailRes] = await Promise.all([
        getShoeboxInbox(),
        getShoeboxEmailAddress()
      ]);
      setItems(inboxRes.data.items || []);
      setShoeboxEmail(emailRes.data.email);
      setInstructions(emailRes.data.instructions || []);
    } catch (error) {
      console.error('Failed to load inbox:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleApprove(item) {
    if (!auth?.user) {
      authPrompt('Approve Receipt');
      return;
    }
    setProcessing(item.id);
    try {
      await approveShoeboxItem(item.id, {
        amount: item.ocr_amount,
        vendor: item.ocr_vendor,
        date: item.ocr_date,
        category: item.ocr_category || 'Business Expense'
      });
      setItems(prev => prev.filter(i => i.id !== item.id));
      toast.success('Receipt approved and expense created!');
    } catch (error) {
      toast.error('Failed to approve');
    } finally {
      setProcessing(null);
    }
  }

  async function handleReject(itemId) {
    try {
      await rejectShoeboxItem(itemId);
      setItems(prev => prev.filter(i => i.id !== itemId));
      toast.success('Item rejected');
    } catch (error) {
      toast.error('Failed to reject');
    }
  }

  async function handleDelete(itemId) {
    if (!window.confirm('Delete this item?')) return;
    try {
      await deleteShoeboxItem(itemId);
      setItems(prev => prev.filter(i => i.id !== itemId));
      toast.success('Item deleted');
    } catch (error) {
      toast.error('Delete failed');
    }
  }

  function copyEmail() {
    if (shoeboxEmail) {
      navigator.clipboard.writeText(shoeboxEmail);
      toast.success('Email copied!');
    }
  }

  const pendingCount = items.filter(i => i.status === 'pending').length;

  return (
    <div className="space-y-6">
      {/* Email Setup Card */}
      <Card className="card-premium bg-gradient-to-br from-blue-500/10 to-purple-500/10">
        <CardContent className="py-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-lg bg-blue-500/20">
                <Mail className="w-8 h-8 text-blue-400" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Email Receipts to Your Inbox</h3>
                <p className="text-muted-foreground text-sm">Forward receipts and invoices to this email. AI will automatically extract the data.</p>
              </div>
            </div>
            {shoeboxEmail && (
              <div className="flex items-center gap-2 bg-background/50 rounded-lg p-3 border border-border">
                <code className="text-sm font-mono text-[hsl(var(--accent))]">{shoeboxEmail}</code>
                <Button variant="ghost" size="sm" onClick={copyEmail}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
          {instructions.length > 0 && (
            <div className="mt-4 pt-4 border-t border-border/50">
              <p className="text-sm font-medium mb-2">How it works:</p>
              <ol className="text-sm text-muted-foreground space-y-1">
                {instructions.map((inst, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-[hsl(var(--accent))]">{i + 1}.</span>
                    {inst}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="flex items-center gap-4">
        <Badge variant="outline" className="text-lg px-4 py-2">
          <Inbox className="w-4 h-4 mr-2" />
          {items.length} Items
        </Badge>
        {pendingCount > 0 && (
          <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 text-lg px-4 py-2">
            {pendingCount} Pending Review
          </Badge>
        )}
        <Button variant="outline" size="sm" onClick={fetchData} className="ml-auto">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Items List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : items.length === 0 ? (
        <Card className="card-premium">
          <CardContent className="py-16 text-center">
            <Inbox className="w-16 h-16 mx-auto text-muted-foreground mb-4 opacity-50" />
            <p className="text-lg font-medium">Inbox Empty</p>
            <p className="text-muted-foreground">Forward receipts to your shoebox email to see them here</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {items.map((item) => (
            <Card key={item.id} className={`card-premium ${item.status === 'pending' ? 'border-yellow-500/30' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  {/* Preview */}
                  <div className="w-20 h-20 rounded-lg bg-muted/30 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {item.file_type?.startsWith('image/') && item.thumbnail ? (
                      <img src={`data:${item.file_type};base64,${item.thumbnail}`} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <FileText className="w-8 h-8 text-muted-foreground" />
                    )}
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium truncate">{item.subject || item.filename || 'Forwarded Receipt'}</p>
                        <p className="text-sm text-muted-foreground">From: {item.from_email || 'Unknown'}</p>
                      </div>
                      <Badge 
                        className={item.status === 'pending' 
                          ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' 
                          : 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                        }
                      >
                        {item.status === 'pending' ? 'Pending' : 'Processed'}
                      </Badge>
                    </div>

                    {/* OCR Data */}
                    {item.ocr_vendor || item.ocr_amount ? (
                      <div className="flex items-center gap-4 mt-2 text-sm">
                        {item.ocr_vendor && (
                          <span className="flex items-center gap-1">
                            <Tag className="w-3 h-3 text-muted-foreground" />
                            {item.ocr_vendor}
                          </span>
                        )}
                        {item.ocr_amount && (
                          <span className="flex items-center gap-1 font-semibold text-[hsl(var(--accent))]">
                            <DollarSign className="w-3 h-3" />
                            {formatCurrency(item.ocr_amount)}
                          </span>
                        )}
                        {item.ocr_date && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Calendar className="w-3 h-3" />
                            {item.ocr_date}
                          </span>
                        )}
                      </div>
                    ) : null}

                    <p className="text-xs text-muted-foreground mt-2">
                      Received: {formatDateTime(item.received_at || item.created_at)}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {item.status === 'pending' && (
                      <Button
                        size="sm"
                        onClick={() => handleApprove(item)}
                        disabled={processing === item.id}
                        className="bg-emerald-500 hover:bg-emerald-600 text-white"
                      >
                        {processing === item.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <><CheckCircle className="w-4 h-4 mr-1" /> Approve</>
                        )}
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => handleReject(item.id)}>
                      <XCircle className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-red-400">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// Tax Resources Tab
function TaxResourcesTab() {
  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-xl font-semibold">Tax Forms, Write-Offs & Business Documents</h2>
        <p className="text-muted-foreground">Search our comprehensive database of tax resources</p>
      </div>
      <TaxFormsSearch />
    </div>
  );
}

export default function DocumentCenter({ context = 'business' }) {
  const auth = useAuth();
  const authPrompt = useAuthPrompt();
  const [activeTab, setActiveTab] = useState('my-documents');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FolderOpen className="w-6 h-6 text-[hsl(var(--accent))]" />
            Document Center
          </h1>
          <p className="text-muted-foreground">
            Manage documents, process with AI, and access tax resources
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-muted/30 p-1">
          <TabsTrigger value="my-documents" className="flex items-center gap-2" data-testid="tab-my-documents">
            <FileStack className="w-4 h-4" />
            My Documents
          </TabsTrigger>
          <TabsTrigger value="email-inbox" className="flex items-center gap-2" data-testid="tab-email-inbox">
            <Mail className="w-4 h-4" />
            Email Inbox
          </TabsTrigger>
          <TabsTrigger value="tax-resources" className="flex items-center gap-2" data-testid="tab-tax-resources">
            <BookOpen className="w-4 h-4" />
            Tax Resources
          </TabsTrigger>
        </TabsList>

        <TabsContent value="my-documents">
          <MyDocumentsTab auth={auth} authPrompt={authPrompt} />
        </TabsContent>

        <TabsContent value="email-inbox">
          <EmailInboxTab auth={auth} authPrompt={authPrompt} />
        </TabsContent>

        <TabsContent value="tax-resources">
          <TaxResourcesTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
